'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { type FC, useEffect, useMemo, useState } from 'react';
import { CURRENCY, SITE } from '@/app/constants';
import { useUserNexus } from '@/app/hooks';
import type { PaidTrialInfo } from '@/app/types';
import { cn, formatAmount } from '@/app/utils';
import { useRetentionStrategy } from './RetentionStrategy';

const pad2 = (value: number) => String(value).padStart(2, '0');

const AnimatedDigit: FC<{ digit: string }> = ({ digit }) => (
  <span className="relative inline-flex h-[1em] w-[0.6em] overflow-hidden">
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.span
        key={digit}
        initial={{ y: '-100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: '100%', opacity: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="absolute inset-0 flex items-center justify-center"
      >
        {digit}
      </motion.span>
    </AnimatePresence>
  </span>
);

const TimeBlock = ({ value, label }: { value: string; label: string }) => (
  <div className="flex shrink-0 flex-col items-center justify-center rounded-16 bg-[rgba(0,0,0,0.24)] p-3 backdrop-blur-[10px]">
    <div className="flex items-center gap-0.5 font-bold text-[36px] text-white tabular-nums leading-none">
      <AnimatedDigit digit={value[0]} />
      <AnimatedDigit digit={value[1]} />
    </div>
    <div className="text-white/40 text-xs">{label}</div>
  </div>
);

type BannerSlide = {
  id: string;
  type: 'discount' | 'trial';
  content: {
    title?: string;
    description?: string;
    banner?: string;
    paidTrial?: PaidTrialInfo;
    showCountdown?: boolean;
  };
};

export const PromoBannerCarousel: FC<{
  className?: string;
  paidTrialInfo?: PaidTrialInfo | null;
}> = ({ className, paidTrialInfo }) => {
  const { isSubOneTimeOffer } = useUserNexus();
  const {
    countdownHours,
    countdownMinutes,
    countdownSeconds,
    phase: retentionPhase,
    daysToExpiry,
  } = useRetentionStrategy();
  const t = useTranslations();

  const is7DaysLeft = retentionPhase === 'phase2';
  const isLastDay = retentionPhase === 'phase3';

  const slides = useMemo<BannerSlide[]>(() => {
    const result: BannerSlide[] = [];

    // Discount banners
    if (isSubOneTimeOffer) {
      result.push({
        id: 'first-discount',
        type: 'discount',
        content: {
          title: t('payments.firstDiscountDialog.title'),
          description: t('payments.firstDiscountDialog.description'),
          banner:
            SITE === 'china'
              ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/first_discount-28af25eb.png'
              : 'https://image01.cf.vidu.studio/vidu/media-asset/first_discount-28af25eb.png',
        },
      });
    }
    if (is7DaysLeft) {
      result.push({
        id: 'retention-7days',
        type: 'discount',
        content: {
          title: t('payments.mySubscription.subscriptionExpired', { daysToExpiry }),
          banner:
            SITE === 'china'
              ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/retention_7days_left-af8fee1d.png'
              : 'https://image01.cf.vidu.studio/vidu/media-asset/retention_7days_left-af8fee1d.png',
        },
      });
    }
    if (isLastDay) {
      result.push({
        id: 'retention-1day',
        type: 'discount',
        content: {
          title: t('payments.mySubscription.todayExpired'),
          banner:
            SITE === 'china'
              ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/retention_1day_left-f29b3bdb.png'
              : 'https://image01.cf.vidu.studio/vidu/media-asset/retention_1day_left-f29b3bdb.png',
          showCountdown: true,
        },
      });
    }

    // Trial banner
    if (paidTrialInfo) {
      result.push({
        id: 'trial',
        type: 'trial',
        content: {
          paidTrial: paidTrialInfo,
        },
      });
    }

    return result;
  }, [isSubOneTimeOffer, is7DaysLeft, isLastDay, daysToExpiry, paidTrialInfo, t]);

  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-rotate every 5 seconds when there are multiple slides
  useEffect(() => {
    if (slides.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) return null;

  const currentSlide = slides[currentIndex];

  return (
    <div className={cn('relative mt-3 sm:mx-4 sm:w-[calc(100%-32px)]', className)}>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="relative flex h-[120px] items-center justify-between overflow-hidden rounded-16 pr-5 pl-10 sm:h-[100px] sm:px-4"
          style={
            currentSlide.type === 'discount'
              ? {
                  backgroundImage: `url(${currentSlide.content.banner})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                }
              : {}
          }
        >
          {currentSlide.type === 'trial' && currentSlide.content.paidTrial && (
            <>
              {/* Trial banner background matching Figma */}
              <div
                className="pointer-events-none absolute inset-0 rounded-[16px] opacity-60"
                style={{
                  background:
                    'radial-gradient(ellipse 120% 200% at 50% -30%, rgba(100,80,200,0.35) 0%, transparent 70%)',
                  filter: 'blur(4px)',
                }}
              />
              <div className="pointer-events-none absolute inset-0 rounded-[16px] bg-black/40" />

              {/* Trial content */}
              <div className="relative z-10 flex flex-col gap-1">
                <div className="font-semibold text-white text-xl sm:text-lg">
                  {t('payments.plans.trialBannerTitle')}
                </div>
                <div className="flex items-baseline gap-2 font-semibold text-xl sm:text-lg">
                  <span className="text-[#b4a5ff]">{currentSlide.content.paidTrial.duration_days}</span>
                  <span className="text-white">{t('payments.plans.trialBannerDuration')}</span>
                  <span className="text-[#b4a5ff]">
                    <span className="text-white">{CURRENCY}</span>
                    {formatAmount(currentSlide.content.paidTrial.price)}
                  </span>
                </div>
              </div>
            </>
          )}

          {currentSlide.type === 'discount' && (
            <>
              <div className="flex flex-col gap-1">
                <div className="font-medium text-white text-xl sm:text-lg">{currentSlide.content.title}</div>
                {currentSlide.content.description && (
                  <div className="text-sm text-system-white48">{currentSlide.content.description}</div>
                )}
              </div>

              {currentSlide.content.showCountdown && (
                <div className="flex items-center gap-3 p-2 sm:hidden">
                  <TimeBlock
                    value={pad2(countdownHours)}
                    label={t('payments.mySubscription.discountBannerCountdownHours')}
                  />
                  <div className="text-[40px] text-system-white40">:</div>
                  <TimeBlock
                    value={pad2(countdownMinutes)}
                    label={t('payments.mySubscription.discountBannerCountdownMinutes')}
                  />
                  <div className="text-[40px] text-system-white40">:</div>
                  <TimeBlock
                    value={pad2(countdownSeconds)}
                    label={t('payments.mySubscription.discountBannerCountdownSeconds')}
                  />
                </div>
              )}
            </>
          )}

          {/* Carousel indicators - positioned at bottom right per Figma */}
          {slides.length > 1 && (
            <div className="absolute right-5 bottom-4 z-20 flex items-center gap-[5px]">
              {slides.map((slide, idx) => (
                <button
                  key={slide.id}
                  type="button"
                  onClick={() => setCurrentIndex(idx)}
                  className={cn(
                    'h-[5px] rounded-full transition-all',
                    idx === currentIndex ? 'w-[40px] bg-white' : 'w-[16px] bg-white/30',
                  )}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
